import { type LawEnforcementAdminContactDto, type LawEnforcementBubbleContextDto } from "@crowdcoursing/api/openapi";
import LanguageIcon from "@mui/icons-material/Language";
import {
  Avatar,
  Box,
  Link as MuiLink,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { type UseQueryResult } from "@tanstack/react-query";

import { EntityAvatar } from "~ui/atoms/EntityAvatar/EntityAvatar";
import { PhoneWithCopyIcon } from "~ui/molecules/PhoneWithCopyIcon/PhoneWithCopyIcon";
import { makeBubbleAvatarUrl } from "~utils/url";

function adminDisplayName(row: LawEnforcementAdminContactDto): string {
  const parts = [row.firstName?.trim(), row.lastName?.trim()].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : "—";
}

export interface LawEnforcementBubbleContextSectionProps {
  bubbleContextQuery: UseQueryResult<LawEnforcementBubbleContextDto>;
  adminContactsQuery: UseQueryResult<LawEnforcementAdminContactDto[]>;
}

export function LawEnforcementBubbleContextSection({
  bubbleContextQuery,
  adminContactsQuery,
}: LawEnforcementBubbleContextSectionProps) {
  const ctx = bubbleContextQuery.data;
  const admins = adminContactsQuery.data ?? [];

  const kindLabel = (k: string | null | undefined): string => {
    if (k === "school") {
      return "School";
    }
    if (k === "district") {
      return "District";
    }
    return "Organization";
  };

  return (
    <Stack spacing={2} sx={{ mb: { sm: 3, xs: 2 }, minWidth: 0 }}>
      <Paper
        elevation={0}
        sx={{
          border: 1,
          borderColor: "divider",
          boxSizing: "border-box",
          maxWidth: "100%",
          p: { sm: 2, xs: 1.5 },
        }}
        variant="outlined"
      >
        {bubbleContextQuery.isLoading ? (
          <Typography color="text.secondary" variant="body2">
            Loading…
          </Typography>
        ) : bubbleContextQuery.isError || !ctx ? (
          <Typography color="text.secondary" variant="body2">
            Could not load site details.
          </Typography>
        ) : (
          <OrganizationInfoReadOnly context={ctx} kindLabel={kindLabel(ctx.bubbleKind ?? undefined)} />
        )}
      </Paper>
      <Paper
        elevation={0}
        sx={{
          border: 1,
          borderColor: "divider",
          boxSizing: "border-box",
          maxWidth: "100%",
          p: { sm: 2, xs: 1.5 },
        }}
        variant="outlined"
      >
        <Typography sx={{ mb: 2 }} variant="subtitle2">
          Administrative Contacts
        </Typography>
        {adminContactsQuery.isLoading ? (
          <Typography color="text.secondary" variant="body2">
            Loading…
          </Typography>
        ) : adminContactsQuery.isError ? (
          <Typography color="text.secondary" variant="body2">
            Could not load admin contacts.
          </Typography>
        ) : admins.length === 0 ? (
          <Typography color="text.secondary" variant="body2">
            No bubble administrators are listed for this site.
          </Typography>
        ) : (
          <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 280 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {admins.map((row, i) => (
                  <TableRow key={`${row.email ?? ""}-${row.phoneNumber ?? ""}-${i}`}>
                    <TableCell>{adminDisplayName(row)}</TableCell>
                    <TableCell>{row.email?.trim() || "—"}</TableCell>
                    <TableCell>
                      {row.phoneNumber?.trim() ? (
                        <PhoneWithCopyIcon phoneNumber={row.phoneNumber.trim()} showPhoneIcon={false} />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Stack>
  );
}

function OrganizationInfoReadOnly({
  context,
  kindLabel,
}: {
  context: LawEnforcementBubbleContextDto;
  kindLabel: string;
}) {
  const name = context.displayName?.trim() || "—";
  const bubbleId = context.bubbleId ?? "";

  return (
    <Stack direction={{ md: "row", xs: "column" }} spacing={2}>
      <Box sx={{ transform: "scale(0.85)", transformOrigin: "top left" }}>
        <EntityAvatar
          canEdit={false}
          entityName={name}
          imageUrl={bubbleId ? makeBubbleAvatarUrl(bubbleId) : undefined}
          imgAlt=""
          size="xlarge"
        />
      </Box>
      <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
        <Stack alignItems="center" direction="row" flexWrap="wrap" gap={2}>
          {context.website ? (
            <MuiLink href={context.website} rel="noopener noreferrer" target="_blank" underline="none">
              <Stack alignItems="center" direction="row" gap={1} sx={{ color: "text.secondary", fontSize: "1.1rem" }}>
                <LanguageIcon color="primary" fontSize="small" />
                Website
              </Stack>
            </MuiLink>
          ) : null}
          {context.nodoWebsite ? (
            <MuiLink href={context.nodoWebsite} rel="noopener noreferrer" target="_blank" underline="none">
              <Stack alignItems="center" direction="row" gap={1} sx={{ color: "text.secondary", fontSize: "1.1rem" }}>
                <Avatar
                  src="https://a1f00b6de6b38043bf8f21f6d4ab5042.cdn.bubble.io/f1689446147188x851842732893250300/nodo_security.ico"
                  sx={{ height: 24, width: 24 }}
                  variant="square"
                />
                Nodo
              </Stack>
            </MuiLink>
          ) : null}
        </Stack>
        <Typography sx={{ mt: 1 }} variant="body1">
          {name}
        </Typography>
        <Typography color="text.secondary" variant="caption">
          {kindLabel}
          {context.slug ? ` · Public slug: ${context.slug}` : null}
        </Typography>
        {context.addressLine1 ? (
          <Typography color="text.secondary" variant="body1">
            {context.addressLine1}
          </Typography>
        ) : null}
        {context.addressLine2 ? (
          <Typography color="text.secondary" variant="body1">
            {context.addressLine2}
          </Typography>
        ) : null}
        {context.addressLine3 ? (
          <Typography color="text.secondary" variant="body1">
            {context.addressLine3}
          </Typography>
        ) : null}
        {[context.city, context.state, context.zipCode].filter(Boolean).length > 0 ? (
          <Typography color="text.secondary" variant="body1">
            {[context.city, context.state, context.zipCode].filter(Boolean).join(", ")}
          </Typography>
        ) : null}
        {context.county ? (
          <Typography color="text.secondary" variant="body1">
            County: {context.county}
          </Typography>
        ) : null}
        {context.phoneNumber ? (
          <Stack alignItems="center" direction="row" flexWrap="wrap" gap={1} sx={{ color: "text.secondary" }}>
            <Typography component="span" variant="body1">
              Phone:
            </Typography>
            <PhoneWithCopyIcon phoneNumber={context.phoneNumber} showPhoneIcon={false} />
          </Stack>
        ) : null}
      </Stack>
    </Stack>
  );
}
